/**
 * Keyboard Navigation Hook
 *
 * Manages keyboard navigation (ArrowUp/Down/Enter/Escape) for search results.
 * Implements ARIA combobox pattern with focus management and scroll-into-view.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { SearchResult } from '../types';

interface UseKeyboardNavigationProps<T extends SearchResult> {
  /** Array of search results to navigate through */
  results: T[];

  /** Callback when a result is selected via Enter key */
  onSelect: (result: T) => void;

  /** Callback when Escape is pressed (close dropdown) */
  onClose: () => void;

  /** Whether the dropdown is currently open */
  isOpen: boolean;
}

interface UseKeyboardNavigationReturn {
  /** Currently focused result index (-1 if none) */
  focusedIndex: number;

  /** Manually set focused index */
  setFocusedIndex: (index: number) => void;

  /** Keyboard event handler to attach to input */
  handleKeyDown: (e: React.KeyboardEvent) => void;

  /** ID of focused element for aria-activedescendant */
  activedescendant: string | undefined;
}

/**
 * Hook for managing keyboard navigation in search dropdown
 */
export function useKeyboardNavigation<T extends SearchResult>({
  results,
  onSelect,
  onClose,
  isOpen,
}: UseKeyboardNavigationProps<T>): UseKeyboardNavigationReturn {
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const resultsRef = useRef<T[]>(results);

  // Update results ref when results change
  useEffect(() => {
    resultsRef.current = results;
  }, [results]);

  // Reset focus when results change or dropdown closes
  useEffect(() => {
    if (!isOpen || results.length === 0) {
      setFocusedIndex(-1);
    } else if (focusedIndex >= results.length) {
      // If focused index is out of bounds after results change, reset to last item
      setFocusedIndex(results.length - 1);
    }
  }, [results.length, isOpen, focusedIndex]);

  // Scroll focused element into view
  useEffect(() => {
    if (focusedIndex >= 0 && focusedIndex < results.length) {
      const element = document.getElementById(`result-${results[focusedIndex].id}`);
      if (element) {
        element.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      }
    }
  }, [focusedIndex, results]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const { key } = e;

      switch (key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex(prev => {
            const next = prev + 1;
            return next >= resultsRef.current.length ? 0 : next; // Wrap to start
          });
          break;

        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex(prev => {
            const next = prev - 1;
            return next < 0 ? resultsRef.current.length - 1 : next; // Wrap to end
          });
          break;

        case 'Enter':
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < resultsRef.current.length) {
            const selectedResult = resultsRef.current[focusedIndex];
            if (selectedResult) {
              onSelect(selectedResult);
            }
          }
          break;

        case 'Escape':
          e.preventDefault();
          onClose();
          break;

        default:
          // For other keys, reset focus to allow typing
          if (focusedIndex !== -1) {
            setFocusedIndex(-1);
          }
          break;
      }
    },
    [focusedIndex, onSelect, onClose],
  );

  const activedescendant =
    focusedIndex >= 0 && focusedIndex < results.length
      ? `result-${results[focusedIndex].id}`
      : undefined;

  return {
    focusedIndex,
    setFocusedIndex,
    handleKeyDown,
    activedescendant,
  };
}
